import { useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Progress } from "@/shared/ui/progress";
import { Skeleton } from "@/shared/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  User,
  ChevronDown,
  ChevronRight,
  Award,
  Calendar,
  Clock,
  AlertTriangle,
  FileText,
  Plus,
  Star,
  TrendingUp,
  CheckCircle2,
  Target,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";

interface Competency {
  id: string;
  name: string;
  description: string | null;
  color: string;
  is_global: boolean;
}

interface FactoryUser {
  id: string;
  full_name: string | null;
  email: string;
  job_title: string | null;
  department: string | null;
}

interface UserCompetency {
  id: string;
  user_id: string;
  competency_id: string;
  certified_at: string;
  expires_at: string | null;
  status: string;
  training_hours: number | null;
  certificate_number: string | null;
  competency: Competency;
}

interface WorkInstruction {
  id: string;
  name: string;
  checklist_kind: "startup" | "shift_change";
  instruction_competencies: {
    competency: { id: string; name: string; color: string | null } | null;
  }[];
}

interface UserCompetencyViewProps {
  users: FactoryUser[];
  userCompetencies: UserCompetency[];
  competencies: Competency[];
  workInstructions: WorkInstruction[];
  isLoading: boolean;
  onAssign: (userId: string) => void;
}

// Certification levels based on number of competencies
const getCertificationLevel = (activeCompetencies: number, totalCompetencies: number) => {
  const percentage = totalCompetencies > 0 ? (activeCompetencies / totalCompetencies) * 100 : 0;
  
  if (percentage >= 90) return { level: "Master", icon: Star, color: "text-warning", bg: "bg-warning/10" };
  if (percentage >= 70) return { level: "Expert", icon: TrendingUp, color: "text-primary", bg: "bg-primary/10" };
  if (percentage >= 50) return { level: "Advanced", icon: Award, color: "text-chart-2", bg: "bg-chart-2/10" };
  if (percentage >= 25) return { level: "Intermediate", icon: CheckCircle2, color: "text-success", bg: "bg-success/10" };
  return { level: "Beginner", icon: Target, color: "text-muted-foreground", bg: "bg-muted" };
};

export function UserCompetencyView({
  users,
  userCompetencies,
  competencies,
  workInstructions,
  isLoading,
  onAssign,
}: UserCompetencyViewProps) {
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

  const toggleUser = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const getUserCompetencies = (userId: string) => {
    return userCompetencies.filter((uc) => uc.user_id === userId);
  };

  const getUserCoveragePercentage = (userId: string) => {
    const userComps = getUserCompetencies(userId);
    const activeComps = userComps.filter((uc) => uc.status === "active");
    return competencies.length > 0
      ? Math.round((activeComps.length / competencies.length) * 100)
      : 0;
  };

  const getExpiringCompetencies = (userId: string) => {
    return getUserCompetencies(userId).filter((uc) => {
      if (!uc.expires_at || uc.status !== "active") return false;
      const daysUntil = differenceInDays(new Date(uc.expires_at), new Date());
      return daysUntil > 0 && daysUntil <= 30;
    });
  };

  const getMissingCompetencies = (userId: string) => {
    const userCompIds = new Set(
      getUserCompetencies(userId)
        .filter((uc) => uc.status === "active")
        .map((uc) => uc.competency_id)
    );
    return competencies.filter((c) => !userCompIds.has(c.id));
  };

  // Get required competencies from work instructions
  const getRequiredCompetencies = (userId: string) => {
    const allRequired = new Set<string>();
    workInstructions.forEach((wi) => {
      wi.instruction_competencies.forEach((wic) => {
        if (wic.competency) {
          allRequired.add(wic.competency.id);
        }
      });
    });

    const userCompIds = new Set(
      getUserCompetencies(userId)
        .filter((uc) => uc.status === "active")
        .map((uc) => uc.competency_id)
    );

    return competencies.filter((c) => allRequired.has(c.id) && !userCompIds.has(c.id));
  };

  // Get total training hours
  const getTotalTrainingHours = (userId: string) => {
    return getUserCompetencies(userId)
      .filter((uc) => uc.status === "active")
      .reduce((sum, uc) => sum + (uc.training_hours || 0), 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <User className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No team members found in this factory</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {users.map((user) => {
        const isExpanded = expandedUsers.has(user.id);
        const coverage = getUserCoveragePercentage(user.id);
        const userComps = getUserCompetencies(user.id).filter((uc) => uc.status === "active");
        const expiring = getExpiringCompetencies(user.id);
        const requiredMissing = getRequiredCompetencies(user.id);
        const trainingHours = getTotalTrainingHours(user.id);
        const certLevel = getCertificationLevel(userComps.length, competencies.length);
        const CertIcon = certLevel.icon;

        return (
          <Collapsible key={user.id} open={isExpanded} onOpenChange={() => toggleUser(user.id)}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardContent className="p-4 cursor-pointer hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">
                          {user.full_name || user.email.split("@")[0]}
                        </p>
                        {/* Certification Level Badge */}
                        <Badge variant="outline" className={`${certLevel.color} border-current text-xs gap-1`}>
                          <CertIcon className="w-3 h-3" />
                          {certLevel.level}
                        </Badge>
                        {expiring.length > 0 && (
                          <Badge variant="outline" className="text-warning border-warning text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {expiring.length} expiring
                          </Badge>
                        )}
                        {requiredMissing.length > 0 && (
                          <Badge variant="outline" className="text-destructive border-destructive text-xs">
                            {requiredMissing.length} required missing
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {user.job_title || user.department || user.email}
                      </p>
                    </div>

                    <div className="hidden sm:flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium">{userComps.length} certifications</p>
                        <p className="text-xs text-muted-foreground">{coverage}% coverage</p>
                      </div>
                      <div className="w-24">
                        <Progress value={coverage} className="h-2" />
                      </div>
                    </div>

                    <Button variant="ghost" size="sm">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="px-4 pb-4 pt-0 border-t border-border">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-4">
                    <div className={`p-3 rounded-lg ${certLevel.bg}`}>
                      <div className="flex items-center gap-2">
                        <CertIcon className={`w-4 h-4 ${certLevel.color}`} />
                        <span className="text-xs text-muted-foreground">Level</span>
                      </div>
                      <p className={`font-semibold ${certLevel.color}`}>{certLevel.level}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Certifications</span>
                      </div>
                      <p className="font-semibold">{userComps.length} / {competencies.length}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Training Hours</span>
                      </div>
                      <p className="font-semibold">{trainingHours}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-primary" />
                        <span className="text-xs text-muted-foreground">Tasks Due</span>
                      </div>
                      <p className="font-semibold">{requiredMissing.length}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Active Competencies */}
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Award className="w-4 h-4 text-success" />
                        Active Certifications
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {userComps.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No active certifications</p>
                        ) : (
                          userComps.map((uc) => (
                            <div
                              key={uc.id}
                              className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: uc.competency.color }}
                                />
                                <span className="text-sm truncate">{uc.competency.name}</span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {uc.expires_at ? (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {format(new Date(uc.expires_at), "MMM yyyy")}
                                  </span>
                                ) : (
                                  <span>No expiry</span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Expiring Soon */}
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-warning" />
                        Expiring Soon
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {expiring.length === 0 ? (
                          <p className="text-xs text-muted-foreground">No expiring certifications</p>
                        ) : (
                          expiring.map((uc) => (
                            <div
                              key={uc.id}
                              className="flex items-center justify-between p-2 rounded-lg bg-warning/10 border border-warning/20"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: uc.competency.color }}
                                />
                                <span className="text-sm truncate">{uc.competency.name}</span>
                              </div>
                              <div className="text-xs text-warning">
                                {differenceInDays(new Date(uc.expires_at!), new Date())} days left
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Required Missing */}
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-destructive" />
                        Required (Missing)
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {requiredMissing.length === 0 ? (
                          <p className="text-xs text-muted-foreground">All required competencies certified</p>
                        ) : (
                          requiredMissing.map((comp) => (
                            <div
                              key={comp.id}
                              className="flex items-center justify-between p-2 rounded-lg bg-destructive/10 border border-destructive/20"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: comp.color }}
                                />
                                <span className="text-sm truncate">{comp.name}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                  </div>

                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" size="sm" onClick={() => onAssign(user.id)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Assign Competency
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        );
      })}
    </div>
  );
}
